import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import AppShell from "../../components/AppShell";
import { Button } from "../../components/ui/button";
import { projectApi, errText } from "../../lib/projectAccess";

// Founder Settings → Project access. Assignment-based field access is OFF by default (decision 2). Turning it on
// changes what superintendents and field supervisors can see, so the dry-run numbers are shown first.
export default function ProjectAccessSettings() {
  const [s, setS] = useState(null);
  const [dry, setDry] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    projectApi.settings().then((r) => setS(r.data)).catch((e) => setErr(errText(e)));
    projectApi.fieldDryRun().then((r) => setDry(r.data)).catch(() => setDry(null));
  }, []);

  const save = async (on) => {
    setBusy(true);
    try {
      const r = await projectApi.saveSettings(on);
      setS(r.data);
      setConfirm(false);
      toast.success(on ? "Assignment-based field access is ON" : "Assignment-based field access is OFF");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const on = !!s?.field_enforcement;
  return (
    <AppShell title="Project access" subtitle="Founder settings">
      <div className="mx-auto w-full max-w-[860px] px-4 md:px-6 space-y-4" data-testid="project-access-settings">
        {err && <div className="text-sm text-red-700" role="alert" data-testid="project-access-error">{err}</div>}
        <section className="bg-white border border-erf-borderCream rounded-md p-4 space-y-3" aria-labelledby="pa-field">
          <h2 id="pa-field" className="font-nav text-sm font-semibold">Assignment-based field access</h2>
          <p className="text-sm" data-testid="project-access-field-state">
            Currently <strong>{s ? (on ? "ON" : "OFF") : "…"}</strong>. While OFF, superintendents and field supervisors keep
            today's access to every project.
          </p>
          <div className="flex gap-2 bg-erf-creamSecondary border border-erf-gold rounded p-3 text-xs" role="note" data-testid="project-access-warning">
            <AlertTriangle className="w-4 h-4 shrink-0 text-erf-dark" aria-hidden="true" />
            <span>When ON, field staff only see projects they are assigned to. Anyone without an assignment loses access
              to field records until a project manager adds them. Review the suggested assignments first.</span>
          </div>
          {dry && (
            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center" data-testid="project-access-dry-run">
              {[["Field staff", dry.field_users], ["Suggested assignments", dry.suggested_assignments],
                ["No verified activity", dry.users_without_activity], ["Not assigned anywhere", dry.users_with_no_assignment]].map(([k, v]) => (
                <div key={k} className="bg-erf-cream border border-erf-borderCream rounded p-2">
                  <dt className="text-[11px] text-erf-dark/70">{k}</dt><dd className="font-mono text-lg">{v}</dd>
                </div>
              ))}
            </dl>
          )}
          <p className="text-[11px] text-erf-dark/60" data-testid="project-access-dry-run-link">
            Dry-run report: {dry?.report || "memory/B2B_DRYRUN_FIELD.md"}. Nothing is granted or revoked by the dry run.
          </p>
          {!confirm ? (
            <Button type="button" disabled={!s || busy} onClick={() => (on ? save(false) : setConfirm(true))}
              className="min-h-[44px] bg-erf-dark text-white" data-testid="project-access-field-toggle">
              {on ? "Turn OFF" : "Turn ON…"}
            </Button>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="project-access-confirm">
              <Button type="button" disabled={busy} onClick={() => save(true)} className="min-h-[44px] bg-erf-gold text-erf-dark font-semibold"
                data-testid="project-access-confirm-on">Yes, turn ON for this company</Button>
              <Button type="button" variant="outline" onClick={() => setConfirm(false)} className="min-h-[44px]"
                data-testid="project-access-confirm-cancel">Cancel</Button>
            </div>
          )}
        </section>
      </div>
    </AppShell>
  );
}
